// ABOUTME: Modal that creates one or more carriers at once: a row per carrier with its name,
// ABOUTME: dimensions, weight and type, a button that adds a row, and one save for all rows.

import { Box, IconButton, useTheme } from "@mui/material"
import { useState } from "react";

import AddIcon from '@mui/icons-material/Add';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { tokens } from "../../../../theme";
import SHModal from "../../../theme/Modal/SHModal";
import Shbutton from "../../../theme/buttons/Shbutton";
import Subheading from "../../../theme/text/Subheading";
import Text from "../../../theme/text/Text";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useAddNewCarriersMutation, useGetCarriersTypesQuery } from "../../../../newapi/carrier/carrierSlice";
import { showToastMessagePostRequest } from "../../../../utils/toasts";
import * as yup from "yup";
import ShValidatedInput from "../../../theme/inputs/ShValidatedInput";
import ShValidatedNumberInput from "../../../theme/inputs/ShValidatedNumberInput";
import ShValidatedInputDropdown from "../../../theme/dropdowns/ShValidatedInputDropdown";
import { yupRequired, yupNumberRequired } from "../../../../utils/validation";

const emptyRow = () => ({
    name: '',
    length: 0,
    width: 0,
    height: 0,
    weight: 0,
    type: ''
});

// The columns of a carrier row, in the order they are shown. Width keeps the rows aligned.
// A number input needs room for its two buttons and the value between them.
const COLUMNS = [
    { key: "name", label: "name", width: 200 },
    { key: "length", label: "length", unit: "mm", width: 160 },
    { key: "width", label: "width", unit: "mm", width: 160 },
    { key: "height", label: "height", unit: "mm", width: 160 },
    { key: "weight", label: "weight", unit: "gram", width: 160 },
    { key: "type", label: "type", width: 180 },
];

const MAddCarrier = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [addCarriers, { isLoading }] = useAddNewCarriersMutation();
    const { data: carrierTypes } = useGetCarriersTypesQuery();

    const [rows, setRows] = useState([emptyRow()]);
    // Errors are keyed "<row index>.<field>", built from the "[0].name" paths yup reports.
    const [fieldErrors, setFieldErrors] = useState({});

    const rowSchema = yup.object().shape({
        name: yupRequired('name', t),
        length: yupNumberRequired('length', t),
        width: yupNumberRequired('width', t),
        height: yupNumberRequired('height', t),
        weight: yupNumberRequired('weight', t),
        type: yupRequired('type', t),
    });
    const schema = yup.array().of(rowSchema).min(1);

    const handleClose = () => {
        setRows([emptyRow()]);
        setFieldErrors({});
        props.handleClose();
    }

    const addRow = () => {
        setRows((prev) => [...prev, emptyRow()]);
    }

    const removeRow = (index) => {
        setRows((prev) => prev.filter((row, rowIndex) => rowIndex !== index));
        setFieldErrors({});
    }

    const handleChange = (index, key, value) => {
        setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
        setFieldErrors((prev) => ({
            ...prev,
            [`${index}.${key}`]: ''
        }));
    }

    const saveCarriers = async () => {
        try {
            if (isLoading) return;

            setFieldErrors({});
            await schema.validate(rows, { abortEarly: false });

            const data = await addCarriers(rows).unwrap();
            showToastMessagePostRequest(t, data);

            handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path.replace(/^\[(\d+)\]\./, "$1.")]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessagePostRequest(t, err);
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const canRemove = rows.length > 1;

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
            sx={{ body: { minWidth: 1160 } }}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("add_carrier")}</Subheading>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2,
                    overflowX:"auto"
                }}
            >
                <Box sx={{ display:"flex", gap:2, alignItems:"flex-end" }}>
                    {COLUMNS.map((column) => (
                        <Box key={column.key} sx={{ width: column.width, flexShrink:0 }}>
                            <Text light>{t(column.label)}</Text>
                            {column.unit && <><Text light>&nbsp;</Text><Text italic light>({t(column.unit)})</Text></>}
                        </Box>
                    ))}
                    <Box sx={{ width: 40, flexShrink:0 }} />
                </Box>

                {rows.map((row, index) => (
                    <Box key={index} sx={{ display:"flex", gap:2, alignItems:"flex-start" }}>
                        <Box sx={{ width: 200, flexShrink:0 }}>
                            <ShValidatedInput
                                name="name"
                                value={row.name}
                                onChange={(key, value) => handleChange(index, key, value)}
                                error={fieldErrors[`${index}.name`]}
                            />
                        </Box>
                        {["length", "width", "height", "weight"].map((key) => (
                            <Box key={key} sx={{ width: 160, flexShrink:0 }}>
                                <ShValidatedNumberInput
                                    name={key}
                                    value={row[key]}
                                    onChange={(field, value) => handleChange(index, field, value)}
                                    error={fieldErrors[`${index}.${key}`]}
                                />
                            </Box>
                        ))}
                        <Box sx={{ width: 180, flexShrink:0 }}>
                            <ShValidatedInputDropdown
                                name="type"
                                useTranslation={true}
                                displayValue={"name"}
                                value={row.type}
                                onChange={(key, value) => handleChange(index, key, value)}
                                error={fieldErrors[`${index}.type`]}
                                options={carrierTypes ?? null}
                                noSelection={t("choose_type")}
                                closeOnSelection
                            />
                        </Box>
                        <Box sx={{ width: 40, flexShrink:0, display:"flex", justifyContent:"center" }}>
                            <IconButton
                                size="small"
                                disabled={!canRemove}
                                onClick={() => removeRow(index)}
                                title={t("delete")}
                                sx={{ color: colors.red[400] }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                ))}

                <Box sx={{ display:"flex", justifyContent:"flex-end" }}>
                    <ButtonGroup version={2}>
                        <Shbutton className={"group-btn"} color={"green"}
                        variant="contained" 
                        startIcon={<AddIcon/>}
                        onClick={addRow}
                        >{t("new")}</Shbutton>
                    </ButtonGroup>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"} color={"green"}
                    variant="contained"
                    onClick={saveCarriers}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MAddCarrier;
